import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { finalize, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { CarouselModule } from 'primeng/carousel';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthService, LoginResponse, isEmpresaAmbiguaError } from '../core/services/auth.service';
import { EmpresaCandidata } from '../core/models/auth.model';
import {
  OTP_ACESSO_PORTAL_NEGADO_STATE_KEY,
  OtpAcessoPortalNegadoState
} from '../core/models/otp.model';
import { EmpresaSelecaoComponent } from '../shared/empresa-selecao/empresa-selecao.component';
import { TourService } from '../shared/tour/tour.service';
import { environment } from 'src/environments/environment';

@Component({
  standalone: true,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [MessageService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    CardModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    CarouselModule,
    ToastModule,
    EmpresaSelecaoComponent
  ]
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private tourService = inject(TourService);
  private messageService = inject(MessageService);

  private readonly baseUrl = environment.apiBaseUrl;
  private readonly rememberKey = 'manager-remember-identificador';

  loading = false;
  loadingTermos = false;
  errorMessage = '';
  errorTermos = '';
  showPassword = false;

  // Termos pendentes (LGPD-11)
  showTermosPanel = false;
  usuarioIdTermos: number | null = null;
  // BUG #68 / BUG #66: token opaco de uso único que o 401 TERMOS_PENDENTES
  // passa a emitir (rollout em andamento — pode não vir ainda). Preferido
  // sobre usuarioId em aceitarTermosLogin(); ausência é tolerada.
  aceiteTermosToken: string | null = null;
  versaoTermoVigente = '1.0';

  // Desambiguação de empresa (F7-00 Fase 4) — aparece só no 409 EMPRESA_AMBIGUA.
  showEmpresaPanel = false;
  empresasCandidatas: readonly EmpresaCandidata[] = [];
  loadingEmpresa = false;
  errorEmpresa = '';
  /**
   * Token opaco de uso único (TTL 5 min) do passo 1. É queimado na primeira
   * tentativa do passo 2 — certa ou errada. Nunca reaproveitado: qualquer
   * falha em `confirmarEmpresa` volta ao passo 1 (senha de novo).
   */
  private desambiguacaoToken: string | null = null;

  slides = [
    {
      titulo: 'Edit Smarter.<br>Export Faster.<br>Create Anywhere.',
      descricao: 'Crie clipes rápidos ou projetos completos com colaboração fluida e performance.',
      imagem: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1600&q=80'
    },
    {
      titulo: 'Colabore em tempo real.<br>Entregue melhor.',
      descricao: 'Fluxos compartilhados, status online e acompanhamento instantâneo em uma só tela.',
      imagem: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80'
    }
  ];
  form = this.fb.nonNullable.group({
    identificador: ['', [Validators.required]],
    senha: ['', [Validators.required, Validators.minLength(5)]],
    remember: [false]
  });

  termosForm = this.fb.nonNullable.group({
    termosAceitos: [false, [Validators.requiredTrue]]
  });

  ngOnInit(): void {
    this.restoreRememberedIdentifier();
    this.carregarVersaoTermos();

    const idParam = this.route.snapshot.queryParamMap.get('identificador');
    if (idParam) {
      this.form.patchValue({ identificador: idParam });
    }

    this.mostrarToastAcessoPortalNegado();
  }

  /**
   * Redirecionamento de `validar-codigo` (esqueci-senha/primeiro-acesso)
   * quando o código confere mas a conta não tem acesso ao portal (spec
   * §4.6-C) — decisão do Marcos: volta pro login já com o toast, em vez de
   * ficar no passo 1 do fluxo de origem. Mesmo padrão de leitura de state
   * do `EsqueciSenhaComponent` (`getCurrentNavigation()` + `history.state`
   * como fallback pra reload).
   *
   * `setTimeout`: `MessageService.add()` emite num `Subject` sem replay —
   * chamado direto aqui, sairia antes do `<p-toast>` filho se inscrever no
   * próprio `ngOnInit` (mesmo bug encontrado em `EsqueciSenhaComponent`).
   */
  private mostrarToastAcessoPortalNegado(): void {
    const state = (this.router.getCurrentNavigation()?.extras.state ?? history.state) as
      | Record<string, unknown>
      | undefined;
    const acessoPortalNegado = state?.[OTP_ACESSO_PORTAL_NEGADO_STATE_KEY] as
      | OtpAcessoPortalNegadoState
      | undefined;

    if (acessoPortalNegado?.mensagem) {
      const mensagem = acessoPortalNegado.mensagem;
      setTimeout(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Acesso não permitido',
          detail: mensagem,
          life: 8000
        });
      });
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.persistRememberedIdentifier();

    const { identificador, senha } = this.form.getRawValue();
    const empresaRef = this.authService.getEmpresaRefHint();

    this.authService
      .login({ identificador, senha, ...(empresaRef ? { empresaRef } : {}) })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => this.onLoginSucesso(response),
        error: (err: HttpErrorResponse) => {
          console.error('Falha no login', err);

          // O 409 EMPRESA_AMBIGUA não é erro genérico — é fluxo de negócio
          // (spec F7-00 §4.1). Precisa ser checado ANTES de qualquer outro
          // tratamento de erro, senão a tela de escolha nunca aparece e o
          // usuário só vê "não foi possível entrar".
          //
          // A senha NÃO é apagada aqui (BUG B3, ver handleAuthError):
          // continua podendo ser precisa mais adiante se o passo 2 cair em
          // TERMOS_PENDENTES e o aceite disparar um retry automático deste
          // submit(). É limpa assim que deixa de ser necessária — ver
          // limparSenha().
          if (isEmpresaAmbiguaError(err)) {
            this.empresasCandidatas = err.error.empresas;
            this.desambiguacaoToken = err.error.desambiguacaoToken;
            this.showEmpresaPanel = true;
            this.errorEmpresa = '';
            this.errorMessage = '';
            return;
          }

          // NOTA DE SEGURANÇA (não reintroduzir):
          // O backend deixou de emitir 'PRIMEIRO_ACESSO_PENDENTE' — Fase 2 do Login (§3)
          // colapsou de propósito os estados pré-senha (usuário inexistente, senha errada
          // e primeiro-acesso-pendente agora respondem igual). Essa mensagem específica ERA
          // o vazamento: permitia oráculo de enumeração de usuários/contas da empresa
          // variando o identificador, incluindo cenário de account-takeover por corrida no
          // primeiro acesso (classificado por @Thor). Se este código está órfão, é porque o
          // contrato mudou — NÃO "conserte" reintroduzindo a mensagem específica; isso reabre
          // o vazamento. O autoatendimento de primeiro acesso agora é sustentado só pelo link
          // incondicional "Primeiro acesso? Crie sua senha" na tela (login.component.html).
          this.handleAuthError(err, () => {
            this.limparSenha();
            this.errorMessage = 'Não foi possível entrar. Verifique usuário e senha.';
          });
        }
      });
  }

  /**
   * Passo 2 do login ambíguo — confirma a empresa escolhida usando o token
   * do passo 1. A senha NÃO é reenviada.
   */
  confirmarEmpresa(ref: string): void {
    if (!this.desambiguacaoToken) return;

    this.loadingEmpresa = true;
    this.errorEmpresa = '';

    this.authService
      .escolherEmpresa({ desambiguacaoToken: this.desambiguacaoToken, empresaRef: ref })
      .pipe(finalize(() => this.loadingEmpresa = false))
      .subscribe({
        next: (response) => {
          this.authService.setEmpresaRefHint(ref);
          this.resetEmpresaPanel();
          this.onLoginSucesso(response);
        },
        error: (err: HttpErrorResponse) => {
          // O token é de uso único — já foi queimado nesta chamada, certa ou
          // errada (spec F7-00 §4.2/§4.3). Não existe "tentar de novo com
          // outra empresa": qualquer falha aqui volta ao passo 1 (senha de
          // novo), nunca reabre este painel com o mesmo token.
          //
          // resetEmpresaPanel(false): não mexe na senha aqui. Quem decide se
          // ela é apagada é handleAuthError — se cair em TERMOS_PENDENTES
          // (a outra conta ambígua também pode ter aceite pendente), ela
          // continua viva para sustentar o retry automático de submit().
          this.resetEmpresaPanel(false);

          this.handleAuthError(err, () => {
            this.limparSenha();
            this.errorMessage =
              'Não foi possível confirmar a empresa. Informe sua senha novamente.';
          });
        }
      });
  }

  /** Ação explícita "trocar empresa" — limpa a dica guardada e volta ao passo 1. */
  trocarEmpresa(): void {
    this.authService.clearEmpresaRefHint();
    this.resetEmpresaPanel();
  }

  aceitarTermosLogin(): void {
    if (this.termosForm.invalid || !this.usuarioIdTermos) {
      this.termosForm.markAllAsTouched();
      return;
    }

    this.loadingTermos = true;
    this.errorTermos = '';

    // BUG #68: contrato real do backend (AceitarTermosInputDto, forbidNonWhitelisted)
    // é { token?, usuarioId?, tipos }. 'versaoTermo'/'tiposTermo' não existem
    // no DTO e derrubavam a validação com 400 em toda chamada — a versão
    // vigente é resolvida no servidor, não enviada aqui. Token tem prioridade
    // sobre usuarioId, nunca os dois (mesmo padrão do primeiro acesso).
    this.http
      .post<void>(`${this.baseUrl}/api/v1/auth/aceitar-termos`, {
        tipos: ['TERMOS_USO', 'POLITICA_PRIVACIDADE'],
        ...(this.aceiteTermosToken ? { token: this.aceiteTermosToken } : { usuarioId: this.usuarioIdTermos })
      })
      .pipe(finalize(() => (this.loadingTermos = false)))
      .subscribe({
        next: () => {
          // Termos aceitos — retenta login automaticamente. Só funciona
          // porque handleAuthError(TERMOS_PENDENTES) manteve a senha viva no
          // form em vez de apagá-la (BUG B3, ver comentário lá): sem ela,
          // este submit() cairia em form.invalid → markAllAsTouched() →
          // volta silenciosamente pro form de login com o campo vermelho e
          // nenhuma explicação, apesar do aceite já ter sido gravado no
          // banco — era o dead-end do #64 reaberto por outra porta.
          // Qualquer desfecho a partir daqui limpa a senha (onLoginSucesso
          // em caso de sucesso; handleAuthError/onCredenciaisInvalidas nos
          // demais; TERMOS_PENDENTES de novo só se a outra conta ambígua
          // também tiver aceite pendente — aí ela segue viva mais uma vez,
          // pelo mesmo motivo).
          this.showTermosPanel = false;
          this.termosForm.reset({ termosAceitos: false });
          this.submit();
        },
        error: () => {
          this.errorTermos = 'Não foi possível registrar o aceite. Tente novamente.';
        }
      });
  }

  cancelarTermos(): void {
    this.showTermosPanel = false;
    this.usuarioIdTermos = null;
    this.aceiteTermosToken = null;
    this.termosForm.reset({ termosAceitos: false });
    this.errorTermos = '';
    // Fluxo abortado pelo usuário — a senha preservada para o retry (BUG B3)
    // deixou de ter uso; não fica viva em memória além do necessário.
    this.limparSenha();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private onLoginSucesso(response: LoginResponse): void {
    this.limparSenha();
    if (!response.usuario.tourConcluido) {
      this.tourService.agendarInicio();
    }
    const perfil = (response.usuario.perfil ?? '').toUpperCase();
    const destino = perfil === 'COLABORADOR' ? '/meu-painel' : '/home';
    this.router.navigate([destino]);
  }

  /**
   * Trata os 2 desfechos de erro comuns aos passos 1 e 2 (ACESSO_PORTAL_NEGADO
   * e TERMOS_PENDENTES têm o mesmo shape nos dois endpoints). Qualquer outro
   * caso (credenciais/token inválido — sempre colapsado numa string simples
   * pelo backend) cai no callback `onCredenciaisInvalidas`, cujo texto varia
   * entre passo 1 e passo 2.
   */
  private handleAuthError(err: HttpErrorResponse, onCredenciaisInvalidas: () => void): void {
    const status = err?.status;
    const body = err?.error as { codigo?: string; usuarioId?: number; aceiteTermosToken?: string } | null;
    const codigo = body && typeof body === 'object' ? body.codigo : undefined;

    if (status === 403 && codigo === 'ACESSO_PORTAL_NEGADO') {
      this.limparSenha();
      this.router.navigate(['/sem-acesso']);
      return;
    }

    if (status === 401 && codigo === 'TERMOS_PENDENTES') {
      // BUG B3 (@Natasha, staging): a senha NÃO é apagada neste branch, de
      // propósito. aceitarTermosLogin() retenta submit() sozinho assim que
      // o aceite é gravado (spec — ver comentário lá); sem a senha viva
      // nesse instante o retry cai em form.invalid e trava em silêncio, com
      // o campo vermelho e nenhuma mensagem, mesmo o aceite já tendo sido
      // persistido — era o dead-end do #64 reaberto por outra porta. Fica
      // em memória só até deixar de ser necessária: cancelarTermos() (fluxo
      // abortado) e onLoginSucesso() (retry deu certo) são os pontos que a
      // apagam.
      this.usuarioIdTermos = body?.usuarioId ?? null;
      // BUG #68: opcional durante o rollout — o backend em produção ainda
      // pode não emitir. Ausência cai no fallback usuarioId em aceitarTermosLogin().
      this.aceiteTermosToken = body?.aceiteTermosToken ?? null;
      this.showTermosPanel = true;
      this.errorMessage = '';
      return;
    }

    onCredenciaisInvalidas();
  }

  /**
   * @param wipeSenha Por padrão apaga a senha do form — correto quando o
   * painel de empresa fecha por sucesso ou por "trocar empresa" (o passo 1
   * exige senha de novo). Passar `false` quando quem chama vai delegar essa
   * decisão a `handleAuthError` (BUG B3): no ramo TERMOS_PENDENTES a senha
   * precisa continuar viva para o retry automático de `aceitarTermosLogin()`.
   */
  private resetEmpresaPanel(wipeSenha = true): void {
    this.showEmpresaPanel = false;
    this.empresasCandidatas = [];
    this.desambiguacaoToken = null;
    this.errorEmpresa = '';
    if (wipeSenha) {
      this.limparSenha();
    }
  }

  /** Único ponto que apaga a senha do form — ver BUG B3 nos chamadores. */
  private limparSenha(): void {
    this.form.patchValue({ senha: '' });
  }

  private carregarVersaoTermos(): void {
    this.http
      .get<{ conteudo: { versao: string } }>(`${this.baseUrl}/api/v1/auth/termos/versao-vigente`)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res?.conteudo?.versao) {
          this.versaoTermoVigente = res.conteudo.versao;
        }
      });
  }

  private persistRememberedIdentifier(): void {
    const ident = this.form.controls.identificador.value.trim();

    if (this.form.controls.remember.value && ident) {
      localStorage.setItem(this.rememberKey, ident);
    } else {
      localStorage.removeItem(this.rememberKey);
    }
  }

  private restoreRememberedIdentifier(): void {
    const saved = localStorage.getItem(this.rememberKey);

    if (saved) {
      this.form.patchValue({ identificador: saved, remember: true });
    }
  }
}
